import React, {useEffect, useState} from 'react';
import {
    useDeleteCategoryMappingMutation,
    useGetAllProductCategoriesQuery,
    useGetCategoryMappingsByContextQuery, useSaveCategoryMappingMutation
} from "../../store/endpoints/ProductCategorizationEndpoints";
import DataGrid from "../DataGrid/DataGrid";
import {GridSelectionMode, IColumnDetails} from "../../models/GridConfiguration";
import {IProductCategory, IProductCategoryMapping} from "../../models/Storefront";
import DropdownField from "../Common/DropdownField";
import {Icon, IconButton, Label, MessageBar, MessageBarType, Panel, PanelType, TooltipHost} from "@fluentui/react";
import MarkdownView from "react-showdown";

interface ICategoryMappingModalProps {
    context: string;
    externalSegments: {segmentName: string, id: string}[]
    externalTypes: {typeName: string, id: string}[]
    getExternalCategory: (segmentId: string, subExtension: string) => Promise<{categoryName: string, id: string}[]>
    subExtension: string;
    segmentFilter?: string;
    isOpen: boolean;
    onClose: () => void;
}

const ExtensionCategoryMappingModal: React.FC<ICategoryMappingModalProps> = (props) => {
    
    const categories = useGetAllProductCategoriesQuery()
    const mappings = useGetCategoryMappingsByContextQuery({context: props.context}, {skip: !props.context})
    
    const [columns, setColumns] = React.useState<IColumnDetails[]>([]);
    const [externalCategoryOptions, setExternalCategoryOptions] = React.useState<{[segmentId: string]: {categoryName: string, id: string}[]}>({});
    const [loadingExternalCategories, setLoadingExternalCategories] = React.useState(false);
    const [saveCategoryMapping] = useSaveCategoryMappingMutation();
    const [saveStatus, setSaveStatus] = useState<string>("");
    const [saveStatusType, setSaveStatusType] = useState<MessageBarType>(MessageBarType.info);
    const [filteredCategories, setFilteredCategories] = useState<IProductCategory[]>([]);
    const [deleteMapping] = useDeleteCategoryMappingMutation()

    useEffect(() => {
        if(props.segmentFilter && categories.data?.items){
            setFilteredCategories(categories.data.items.filter(cat => cat.segmentId?.normalizeIdentifier() === props.segmentFilter));
        }
        else if(categories.data?.items) {
            setFilteredCategories(categories.data.items);
        }
    }, [props.segmentFilter, categories.data]);

    // Hide the save message after a few seconds if successful
    useEffect(() => {
        if (saveStatusType === MessageBarType.success && saveStatus) {
            const timeout = setTimeout(() => {
                setSaveStatus("");
            }, 2500);
            return () => clearTimeout(timeout);
        }
    }, [saveStatus, saveStatusType]);

    useEffect(() => {
        async function fetchAllExternalCategories() {
            if (!mappings.data?.items) return;
            const segmentIds = Array.from(new Set(mappings.data.items.map(m => m.externalSegmentId).filter(Boolean)));
            const options: {[segmentId: string]: {categoryName: string, id: string}[]} = {};
            await Promise.all(segmentIds.map(async segmentId => {
                if(!segmentId || !props.subExtension) return;
                options[segmentId] = await props.getExternalCategory(segmentId, props.subExtension);
            }));
            setExternalCategoryOptions(options);
        }
        fetchAllExternalCategories().then();
    }, [mappings.data, props.context, props.subExtension]);

    useEffect(() => {
        setColumns([
            {
                index: 0,
                identifier: 'delete',
                label: '',
                type: 'Text',
                required: true,
                component: (v: IProductCategory) => {
                    const mappingId = v.segmentId && v.id ? 
                        `${props.context.normalizeIdentifier()}-${v.segmentId.normalizeIdentifier()}-${v.id}` 
                        : undefined;
                    if(mappingId && mappings.data?.items.find((mapping) => mapping.id === mappingId)){
                        return(
                        <IconButton iconProps={{iconName: 'delete'}} onClick={() => {
                            if (v.id) {
                                deleteMapping({
                                    id: mappingId,
                                    context: props.context.normalizeIdentifier()
                                }).then((result) => {
                                    if ('data' in result && result.data.success) {
                                        setSaveStatus("Mapping cleared");
                                        setSaveStatusType(MessageBarType.success);
                                    } else {
                                        setSaveStatus("Failed to clear mapping");
                                        setSaveStatusType(MessageBarType.error);
                                    }
                                });
                            }
                        }}/>
                        )
                    }
                    return <></>;
                }
            },
            {
                index: 1,
                identifier: 'segment',
                label: 'Segment',
                type: 'Text',
                required: true,
                component: (v: IProductCategory) => <div>{v.segmentId}</div>
            },
            {
                index: 2,
                identifier: 'category',
                label: 'Category',
                type: 'Text',
                required: true,
                component: (v: IProductCategory) => <div>{v.identifier}</div>
            },
            {
                index: 3,
                identifier: 'externalType',
                label: 'External Product Type',
                type: 'Text',
                required: true,
                component: (cat: IProductCategory) => {
                    const mappingId = `${props.context.normalizeIdentifier()}-${cat.segmentId?.normalizeIdentifier()}-${cat.identifier?.normalizeIdentifier()}`;
                    let mappingForCategory = mappings.data?.items.find((mapping) => mapping.id === mappingId);
                    const mappingForSegment = mappings.data?.items.find((mapping) => mapping.id === `${props.context.normalizeIdentifier()}-${cat.segmentId?.normalizeIdentifier()}`);

                    let selectedKey = mappingForCategory?.externalProductTypeId;
                    let keyFromDefault = false;
                    if(!selectedKey && mappingForSegment?.externalProductTypeId) {
                        selectedKey = mappingForSegment.externalProductTypeId;
                        keyFromDefault = true;
                    }
                    return (
                        <div>
                            <DropdownField
                                statusIcon={keyFromDefault ? 'Info' : undefined}
                                statusMessage={keyFromDefault ? 'Value inherited from segment-level mapping' : undefined}
                                placeholder={props.externalTypes.length === 0 ? 'Waiting for Product Types' : 'Select Product Type'}
                                selectedKey={selectedKey}
                                disabled={props.externalTypes.length === 0}
                                options={props.externalTypes.map((cat) => ({ key: cat.id, text: cat.typeName }))}
                                onChange={(e, v) => {
    
                                    if(!mappingForCategory){
                                        mappingForCategory = { id: '', segment: cat?.segmentId ?? '', category: cat?.id, externalSegmentId: mappingForSegment?.externalSegmentId ?? '', context: props.context }
                                    }
    
                                    saveCategoryMapping({mapping: {...mappingForCategory, externalProductTypeId: v?.key as string}, context: props.context }).then((result) => {
                                        if ('data' in result) {
                                            setSaveStatus("Mapping saved successfully");
                                            setSaveStatusType(MessageBarType.success);
                                        } else {
                                            setSaveStatus("Failed to save mapping");
                                            setSaveStatusType(MessageBarType.error);
                                        }
                                    });
                                }}
                            />
                        </div>
                    );
                }
            },
            {
                index: 4,
                identifier: 'externalSegment',
                label: 'External Category',
                type: 'Text',
                required: true,
                component: (cat: IProductCategory) => {
                    // Lookup mapping using consistent id logic
                    const mappingId = `${props.context.normalizeIdentifier()}-${cat.segmentId?.normalizeIdentifier()}-${cat.identifier?.normalizeIdentifier()}`;
                    let mappingForCategory = mappings.data?.items.find((mapping) => mapping.id === mappingId);

                    const mappingForSegment = mappings.data?.items.find((mapping) => mapping.id === `${props.context.normalizeIdentifier()}-${cat.segmentId?.normalizeIdentifier()}`);
                    
                    let selectedKey = mappingForCategory?.externalSegmentId;
                    let keyFromDefault = false;
                    if(!selectedKey && mappingForSegment?.externalSegmentId) {
                       selectedKey = mappingForSegment.externalSegmentId;
                       keyFromDefault = true;
                    }
                    return (
                        <DropdownField
                            statusIcon={keyFromDefault ? 'Info' : undefined}
                            statusMessage={keyFromDefault ? 'Value inherited from segment-level mapping' : undefined}
                            selectedKey={selectedKey}
                            disabled={props.externalSegments.length === 0}
                            placeholder={props.externalSegments.length === 0 ? 'Waiting for Categories' : 'Select Category'}
                            options={props.externalSegments.map((cat) => ({ key: cat.id, text: cat.segmentName }))}
                            onChange={(e, v) => {
                                const extSegValue = (!v?.key ||  v?.key === '') ? undefined : v?.key as string;
                                // Always construct mapping id consistently
                                const newMappingId = `${props.context.normalizeIdentifier()}-${cat.segmentId?.normalizeIdentifier()}-${cat.identifier?.normalizeIdentifier()}`;
                                if(!mappingForCategory) {
                                    mappingForCategory = {
                                        id: newMappingId,
                                        segment: cat?.segmentId?.normalizeIdentifier() ?? '',
                                        category: cat?.id,
                                        externalSegmentId: extSegValue,
                                        externalCategoryId: undefined,
                                        context: props.context
                                    }
                                } else {
                                    mappingForCategory = {
                                        ...mappingForCategory,
                                        externalSegmentId: extSegValue
                                    }
                                }
                                saveCategoryMapping({mapping: mappingForCategory, context: props.context }).then((result) => {
                                    if ('data' in result) {
                                        setSaveStatus("Mapping saved successfully");
                                        setSaveStatusType(MessageBarType.success);
                                        // Optionally, trigger a refresh of mappings here if needed
                                    } else {
                                        setSaveStatus("Failed to save mapping");
                                        setSaveStatusType(MessageBarType.error);
                                    }
                                });
                            }}
                        />
                    );
                }
            },
            {
                index: 5,
                identifier: 'externalCategory',
                label: 'External Sub-Category',
                type: 'Text',
                required: true,
                component: (cat: IProductCategory) => {
                    const mappingId = `${props.context.normalizeIdentifier()}-${cat.segmentId?.normalizeIdentifier()}-${cat.identifier?.normalizeIdentifier()}`;
                    let mappingForCategory = mappings.data?.items.find((mapping) => mapping.id === mappingId);
                    const mappingForSegment = mappings.data?.items.find((mapping) => mapping.id === `${props.context.normalizeIdentifier()}-${cat.segmentId?.normalizeIdentifier()}`);
                    
                    let selectedSegmentKey = mappingForCategory?.externalSegmentId
                    if(!selectedSegmentKey && mappingForSegment?.externalSegmentId) {
                        selectedSegmentKey = mappingForSegment.externalSegmentId;
                    }

                    const categoryOptions = selectedSegmentKey ? externalCategoryOptions[selectedSegmentKey] ?? [] : [];
                    return (
                        <DropdownField
                            disabled={!selectedSegmentKey || categoryOptions.length === 0}
                            placeholder={!selectedSegmentKey ? 'Must Select Category First' : (categoryOptions.length === 0 ? 'Waiting for Sub-Categories' : 'Select Sub-Category')}
                            selectedKey={mappingForCategory?.externalCategoryId}
                            options={[{key: '', text: '[No Selection]'},...categoryOptions.map((cat) => ({ key: cat.id, text: cat.categoryName }))]}
                            onChange={(_, v) => {
                                if(!mappingForCategory){
                                    mappingForCategory = { id: '', segment: cat?.segmentId?.normalizeIdentifier() ?? '', category: cat?.id, externalSegmentId: selectedSegmentKey ?? '', context: props.context }
                                }
                                const extCatValue = (!v?.key ||  v?.key === '') ? undefined : v?.key as string;
                                saveCategoryMapping({mapping: {...mappingForCategory, externalCategoryId: extCatValue}, context: props.context }).then((result) => {
                                    if ('data' in result) {
                                        setSaveStatus("Mapping saved successfully");
                                        setSaveStatusType(MessageBarType.success);
                                    } else {
                                        setSaveStatus("Failed to save mapping");
                                        setSaveStatusType(MessageBarType.error);
                                    }
                                });
                                setLoadingExternalCategories(false)
                            }}
                        />
                    );
                }
            }
        ]);
    }, [mappings, props.externalSegments, externalCategoryOptions, props.externalTypes])

    return (
        <Panel
            onOuterClick={() => {
                console.log("swallowing modal close event")
            }}
            isOpen={props.isOpen}
            headerText={'Category Mappings'}
            isFooterAtBottom={true}
            type={PanelType.large}
            onDismiss={props.onClose}
        >
            <div>
                <DataGrid items={filteredCategories ?? []} selectionMode={GridSelectionMode.none} loading={categories.isLoading || categories.isFetching} getItemId={(item: IProductCategoryMapping) => item.id} overrideColumns={columns} />
                {saveStatus && (
                    <MessageBar messageBarType={saveStatusType} isMultiline={false} styles={{root: {marginLeft: 8, minWidth: 200}}}>
                        {saveStatus}
                    </MessageBar>
                )}
            </div>
        </Panel>
    )
}

export default ExtensionCategoryMappingModal;

